<template>
  <div class="radius">
    <br />

    <b-card no-body class="custom-accordion shadow-none mb-3">
      <b-card-header header-tag="header" role="tab" class="bg-white">
        <div class="float-right">
          <div class="d-flex justify-content-end" style="margin-bottom: 1.70rem;">
            <div class="d-flex align-items-center" @click="goBack()">
              <div class="new-task-text">{{ $t("data-breach.list") }} <i class="bx bx-chevron-right font-size-24 align-middle"></i></div>
            </div>
          </div>

          <div v-if="form._id" class="d-flex justify-content-end" style="margin-bottom: 1.70rem;">
            <div class="d-flex align-items-center">
              <b-dropdown split split-href="#" :text="$t('task.download-options')" size="sm" variant="light" class="mr-4">
                <b-dropdown-item @click="exportAsWord" href="#">{{ $t("data-breach.export-as-word") }}</b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </div>

        <div class="row">
          <h3>
            {{ $t("data-breach.definition") }}
          </h3>
        </div>
      </b-card-header>
      <b-card-body>
        <div class="form-row">
          <div class="form-group col-md-12">
            <label>{{ $t("data-breach.name") }}</label>
            <span
              v-if="$v.form.name.$error"
              :class="{
                'text-danger': $v.form.name.$error,
              }"
            >
              <small> {{ $t("validation.required") }}</small>
            </span>
            <input
              type="text"
              class="form-control"
              v-model="form.name"
              :class="{
                'is-invalid': $v.form.name.$error,
              }"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label>{{ $t("data-breach.role.text") }}</label>
            <span
              v-if="$v.form.role.$error"
              :class="{
                'text-danger': $v.form.role.$error,
              }"
            >
              <small> {{ $t("validation.required") }}</small>
            </span>
            <select
              v-model="form.role"
              class="form-control"
              :class="{
                'is-invalid': $v.form.role.$error,
              }"
            >
              <option value="ORGANIZATION_DATA_PROTECTION_PERSON">{{ $t("data-breach.role.dpo") }}</option>
              <option value="ORGANIZATION_CONTACT_PERSON">
                {{ $t("data-breach.role.contact") }}
              </option>
              <option value="OTHER">{{ $t("data-breach.role.other") }}</option>
            </select>
          </div>

          <div class="form-group col-md-4">
            <label> {{ $t("data-breach.detected-by") }} </label>
            <span>
              <small> {{ $t("validation.required") }}</small>
            </span>
            <p>{{ form.detectedBy ? form.detectedBy : "-" }}</p>
          </div>

          <div class="form-group col-md-4">
            <label> {{ $t("common.email") }} </label>
            <span>
              <small> {{ $t("validation.required") }}</small>
            </span>

            <p>{{ form.email ? form.email : "-" }}</p>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label>{{ $t("data-breach.data-responsible-title") }}</label>
            <p>
              {{ form.dataProtectionName ? form.dataProtectionName : "-" }}
            </p>
          </div>

          <div class="form-group col-md-4">
            <label>{{ $t("data-breach.data-responsible-address") }}</label>
            <p>
              {{ form.dataProtectionAddress ? form.dataProtectionAddress : "-" }}
            </p>
          </div>
        </div>
        <div v-show="showDataProtectionAlert">
          <b-alert show variant="danger"> {{ $t("data-breach.data-responsible-required-description") }}<br /> </b-alert>
          <right-panel
            class="mb-2 mr-2"
            id="sidebar-new-organization"
            width="600px"
            icon="bx bx-plus"
            :text="$t('organization.edit')"
            @click="$store.commit('organization/editedOrganization', this.currentOrganization)"
            @hidden="rightPanelClosed"
          >
            <div class="pl-2 pr-2">
              <OrganizationForm :hide-library="true" />
            </div>
          </right-panel>
        </div>

        <div>
          <h4>
            <label>
              {{ $t("data-breach.question-personal-data-effected") }}
              <b-form-checkbox v-model="form.questionPersonalDataEffected" class="float-right ml-1" />
            </label>
          </h4>
          <b-alert show variant="info" v-html="$t('data-breach.question-personal-data-effected')"> </b-alert>
        </div>
        <hr />
        <div>
          <h4>
            <label>
              {{ $t("data-breach.question-create-risk") }}
              <b-form-checkbox v-model="form.questionCreateRisk" class="float-right ml-1" />
            </label>
          </h4>

          <div v-if="form.questionCreateRisk">
            <span v-b-modal="'risk-modal-select'" class="text-primary">
              <i class="bx bx-search font-size-16 align-middle"></i>
              {{ $t("data-breach.add-risk") }}
            </span>
            <span class="ml-3 mr-3"> {{ $t("common.or") }}</span>

            <router-link :to="`/${currentProduct.toLowerCase()}/risk/create`" target="_blank">
              <i class="fa fa-plus"></i>
              {{ $t("data-breach.create-new-risk") }}
            </router-link>
            <div v-if="form.risks" class="mt-2">
              <b-card v-for="r in form.risks" :key="r._id" class="border border-primary m-1 mr-4">
                <router-link :to="`/risk/${r._id}`" target="_blank">
                  <i class="fa fa-link"></i>
                  {{ r.name | translate }}
                </router-link>
              </b-card>
            </div>
          </div>
          <ModalSelect
            id="risk-modal-select"
            multiple="true"
            :title="$t('data-breach.question-create-risk')"
            store="organizationRisk"
            @selected="riskSelected"
            :requestQuery="productQuery"
          />
        </div>

        <hr />
        <div>
          <h4>
            <label>
              {{ $t("data-breach.question-has-danger-risk") }}
              <b-form-checkbox v-model="form.questionHasDangerRisk" class="float-right ml-1" />
            </label>
          </h4>
          <b-alert show variant="info">
            {{ $t("data-breach.question-has-danger-risk-alert") }}
          </b-alert>
        </div>

        <hr />

        <h3 class="mb-3">{{ $t("data-breach.general-info") }}</h3>
        <div>
          <div>
            <label v-b-modal="'source-and-type-modal-select'">
              <i class="bx bx-search font-size-16 align-middle"></i>
              {{ $t("data-breach.source-and-type.info") }}
            </label>
            <span
              v-if="$v.form.sourceAndType.$error"
              :class="{
                'text-danger': $v.form.sourceAndType.$error,
              }"
            >
              <br />
              <small>{{ $t("validation.required") }}</small>
            </span>
            <textarea
              :class="{
                'is-invalid': $v.form.sourceAndType.$error,
              }"
              v-model="form.sourceAndType"
              rows="5"
              class="form-control"
            ></textarea>

            <ModalSelect
              id="source-and-type-modal-select"
              multiple="true"
              :title="$t('data-breach.source-and-type.name')"
              store="sourceAndType"
              @selected="sourceAndTypeDefinitionSelected"
            />
          </div>
        </div>

        <h3 class="mb-3 mt-5">{{ $t("data-breach.date-time-info") }}</h3>
        <div>
          <h5>{{ $t("data-breach.breach-date-info") }}</h5>
          <b-row>
            <b-col md="4">
              <label>{{ $t("data-breach.date") }}</label>

              <date-picker
                v-model="form.breachDate"
                :first-day-of-week="1"
                :lang="currentLanguage"
                format="DD.MM.YYYY"
                value-type="timestamp"
                :placeholder="$t('data-breach.date')"
              />
            </b-col>
            <b-col md="4">
              <label>{{ $t("data-breach.time") }}</label>
              <b-form-timepicker v-model="form.breachTime" locale="tr" />
            </b-col>
            <b-col md="4">
              <label>{{ $t("data-breach.location") }}</label>

              <filter-select
                id="countriesFilter"
                ref="countriesFilter"
                :placeholder="$t('country.select')"
                :multiple="false"
                @filter="onCountriesSearch"
                @changed="onCountriesSelect"
                :selected="currentOrganization.country"
                track="code"
              />
            </b-col>
          </b-row>

          <h5 class="mt-4">{{ $t("data-breach.breach-detect-date-info") }}</h5>
          <b-row>
            <b-col md="4">
              <label>{{ $t("data-breach.date") }}</label>

              <date-picker
                v-model="form.breachDetectDate"
                :first-day-of-week="1"
                :lang="currentLanguage"
                format="DD.MM.YYYY"
                value-type="timestamp"
                :placeholder="$t('data-breach.date')"
              />
            </b-col>
            <b-col md="4">
              <label>{{ $t("data-breach.time") }}</label>
              <b-form-timepicker v-model="form.breachDetectTime" locale="tr" />
            </b-col>
          </b-row>
        </div>

        <h3 class="mb-3 mt-5">{{ $t("data-breach.possible-effect-info") }}</h3>
        <div>
          <div>
            <label v-b-modal="'possible-effect-modal-select'">
              <i class="bx bx-search font-size-16 align-middle"></i>
              {{ $t("data-breach.possible-effect") }}
            </label>
            <textarea v-model="form.possibleEffect" rows="5" class="form-control"></textarea>

            <ModalSelect
              id="possible-effect-modal-select"
              multiple="true"
              :title="$t('data-breach.source-and-type.name')"
              store="possibleEffect"
              @selected="possibleEffectDefinitionSelected"
            />
          </div>
        </div>
        <div class="mt-4">
          <label>{{ $t("data-breach.data-categories") }} </label>

          <filter-select
            :placeholder="$t('data-category.library')"
            :selected="form.dataCategories"
            ref="dataCategoryFilter"
            :multiple="true"
            @filter="dataCategoryFilterSearch"
            @changed="dataCategoryFilterChanged"
          />
        </div>

        <div class="mt-4">
          <label>{{ $t("data-breach.data-subjects") }} </label>

          <filter-select
            :placeholder="$t('datasubject.library')"
            ref="dataSubjectFilter"
            :selected="form.dataSubjects"
            :multiple="true"
            @filter="dataSubjectFilterSearch"
            @changed="dataSubjectFilterChanged"
          />
        </div>

        <div class="mt-4">
          <label class="inline-block" v-b-modal="'electronic-effected-systems-modal-select'">
            <i class="bx bx-search font-size-16 align-middle"></i>
            {{ $t("data-breach.electronic-effected-systems") }}
          </label>

          <label class="inline-block ml-4" v-b-modal="'physical-effected-systems-modal-select'">
            <i class="bx bx-search font-size-16 align-middle"></i>
            {{ $t("data-breach.physical-effected-systems") }}
          </label>

          <textarea v-model="form.effectedSystems" rows="5" class="form-control"></textarea>

          <ModalSelect
            id="electronic-effected-systems-modal-select"
            multiple="true"
            :title="$t('data-breach.electronic-effected-systems')"
            store="organizationElectronicStorage"
            @selected="effectedSystemsDefinitionSelected"
          />

          <ModalSelect
            id="physical-effected-systems-modal-select"
            multiple="true"
            :title="$t('data-breach.physical-effected-systems')"
            store="organizationPhysicalStorage"
            @selected="effectedSystemsDefinitionSelected"
          />
        </div>

        <div class="mt-4">
          <b-row>
            <b-col md="6">
              <label>{{ $t("data-breach.effected-person-count") }}</label>
              <input v-model="form.effectedPersonCount" class="form-control" type="number" />
            </b-col>

            <b-col md="6">
              <label>{{ $t("data-breach.effected-record-count") }}</label>
              <input v-model="form.effectedRecordCount" class="form-control" type="number" />
            </b-col>
          </b-row>
        </div>

        <h3 class="mb-3 mt-5">{{ $t("data-breach.policy-info") }}</h3>

        <div class="mt-4">
          <label v-b-modal="'before-policies-modal-select'">
            <i class="bx bx-search font-size-16 align-middle"></i>
            {{ $t("data-breach.before-policies") }}
          </label>
          <textarea v-model="form.beforePolicies" rows="5" class="form-control"></textarea>

          <ModalSelect
            id="before-policies-modal-select"
            multiple="true"
            :title="$t('data-breach.before-policies')"
            store="organizationPolicy"
            @selected="beforePolicyDefinitionSelected"
            :requestQuery="productQuery"
          />
        </div>

        <div class="mt-4">
          <label v-b-modal="'after-policies-modal-select'">
            <i class="bx bx-search font-size-16 align-middle"></i>
            {{ $t("data-breach.after-policies") }}
          </label>
          <textarea v-model="form.afterPolicies" rows="5" class="form-control"></textarea>

          <ModalSelect
            id="after-policies-modal-select"
            multiple="true"
            :title="$t('data-breach.after-policies')"
            store="organizationPolicy"
            @selected="afterPolicyDefinitionSelected"
            :requestQuery="productQuery"
          />
        </div>

        <div class="mt-4">
          <label>
            {{ $t("data-breach.question-want-to-notificate") }}
            <b-form-checkbox v-model="form.questionWantToNotificate" class="float-right ml-1" />
          </label>
        </div>

        <div v-if="form.questionWantToNotificate">
          <h3 class="mb-3 mt-5">{{ $t("data-breach.notification-info") }}</h3>

          <div class="mt-4">
            <b-row>
              <b-col md="6">
                <label>{{ $t("data-breach.notification-type") }}</label>

                <filter-select
                  :placeholder="$t('data-breach.notification-type')"
                  ref="notificationTypeFilter"
                  :multiple="false"
                  @filter="notificationTypeFilterSearch"
                  @changed="notificationTypeFilterChanged"
                />
              </b-col>
              <b-col md="6">
                <label>{{ $t("data-breach.notification-date") }}</label>

                <b-form-datepicker
                  v-model="form.notificationDate"
                  :date-format-options="{
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  }"
                  locale="tr"
                />
              </b-col>
            </b-row>

            <b-alert show variant="info" class="mt-2">
              {{ $t("data-breach.notification-alert") }}
            </b-alert>
          </div>

          <div class="mt-4">
            <label>
              {{ $t("data-breach.delay-info") }}
            </label>

            <textarea v-model="form.delayExcuse" rows="5" class="form-control"></textarea>

            <b-alert show variant="info" class="mt-2">
              {{ $t("data-breach.delay-alert") }}
            </b-alert>
          </div>

          <div class="mt-4">
            <label>
              {{ $t("data-breach.processing-adjective-info") }}
            </label>

            <filter-select
              :placeholder="$t('processing-adjective.text')"
              ref="processingAdjectiveFilter"
              :multiple="false"
              @filter="processingAdjectiveFilterSearch"
              @changed="processingAdjectiveFilterChanged"
            />

            <b-alert show variant="info" class="mt-2">
              {{ $t("data-breach.processing-adjective-info-alert") }}
            </b-alert>
          </div>

          <div class="mt-4">
            <label>
              {{ $t("data-breach.question-want-to-domestic-information") }}
              <b-form-checkbox v-model="form.questionWantToDomesticInformation" class="float-right ml-1" />
            </label>

            <textarea v-if="form.questionWantToDomesticInformation" v-model="form.wantToDomesticInformationDescription" rows="5" class="form-control"></textarea>
          </div>

          <div class="mt-4" v-if="form.questionWantToDomesticInformation">
            <label v-b-modal="'domestic-information-modal-select'">
              <i class="bx bx-search font-size-16 align-middle"></i>
              {{ $t("data-breach.domestic-information") }}
            </label>
            <textarea v-model="form.domesticInformation" rows="5" class="form-control"></textarea>

            <ModalSelect
              id="domestic-information-modal-select"
              multiple="true"
              :title="$t('data-breach.domestic-information')"
              store="domesticThirdParty"
              @selected="domesticInformationSelected"
            />

            <b-alert show variant="info" class="mt-2">
              {{ $t("data-breach.domestic-information-alert") }}
            </b-alert>
          </div>

          <div class="mt-4">
            <label>
              {{ $t("data-breach.question-want-to-abroad-information") }}
              <b-form-checkbox v-model="form.questionWantToAbroadInformation" class="float-right ml-1" />
            </label>

            <textarea v-if="form.questionWantToAbroadInformation" v-model="form.wantToAbroadInformationDescription" rows="5" class="form-control"></textarea>
          </div>

          <div class="mt-4" v-if="form.questionWantToAbroadInformation">
            <label v-b-modal="'abroad-information-modal-select'">
              <i class="bx bx-search font-size-16 align-middle"></i>

              {{ $t("data-breach.abroad-information") }}
            </label>
            <textarea v-model="form.abroadInformation" rows="5" class="form-control"></textarea>

            <ModalSelect
              id="abroad-information-modal-select"
              multiple="true"
              :title="$t('data-breach.abroad-information')"
              store="abroadThirdParty"
              @selected="abroadInformationSelected"
            />

            <b-alert show variant="info" class="mt-2">
              {{ $t("data-breach.abroad-information-alert") }}
            </b-alert>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <sweet-button @click="save()" buttonClass="ml-2" :loading="isUpdating" variant="success" size="lg" icon="mdi mdi-check">
      {{ $t("common.save") }}
    </sweet-button>
  </div>
</template>

<script>
import ModalSelect from "@c/modal-select";
import OrganizationForm from "@modules/organization/organization-form";
import DatePicker from "vue2-datepicker";
import { required } from "vuelidate/lib/validators";
import Countries from "../countries/countries.json";

export default {
  components: { ModalSelect, DatePicker, OrganizationForm },
  data() {
    return {
      reloadMessage: this.$t("common.reload-message"),
      isThereUnsavedChange: false,
      form: { name: undefined, sourceAndType: [], risks: [], dataCategories: [], dataSubjects: [] },
    };
  },
  validations: {
    form: {
      name: { required },
      role: { required },
      sourceAndType: { required },
      dataProtectionName: { required },
      dataProtectionAddress: { required },
    },
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.form = {
          ...this.prepareFormForEdit(v, [
            "breachLocation.name",
            "sourceAndType",
            "possibleEffect",
            "effectedSystems",
            "beforePolicies",
            "afterPolicies",
            "delayExcuse",
            "wantToDomesticInformationDescription",
            "wantToAbroadInformationDescription",
            "domesticInformation",
            "AbroadInformation",
          ]),
          breachDate: new Date(v.breachDate).getTime(),
          breachDetectDate: new Date(v.breachDetectDate).getTime(),
        };
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(v) {
        if (v) {
          this.isThereUnsavedChange = true;
        }
      },
    },
    "form.role": {
      deep: true,
      immediate: true,
      handler() {
        this.roleWatcher();
      },
    },
    showDataProtectionAlert: {
      deep: true,
      immediate: true,
      handler(v) {
        if (v) {
          this.$store.commit("organization/editedOrganization", this.currentOrganization);
        }
      },
    },
  },
  computed: {
    dataBreach() {
      return this.$store.getters["organizationDataBreach/dataBreach"];
    },
    isUpdating() {
      return this.$store.getters["organizationDataBreach/isUpdating"];
    },
    editedEntity() {
      return this.$store.getters["organizationDataBreach/editedEntity"];
    },

    isEditMode() {
      return this.$store.getters["organizationDataBreach/isEditMode"];
    },

    isCoping() {
      return this.$store.getters["organizationDataBreach/isCoping"];
    },
    showDataProtectionAlert() {
      return this.$v.form.dataProtectionAddress.$error || this.$v.form.dataProtectionName.$error;
    },
    productQuery() {
      return {
        products: { value: this.currentProduct, isObject: true },
      };
    },
  },
  async mounted() {
    this.beforeUnload = this.beforeUnload.bind(this);
    window.addEventListener("beforeunload", this.beforeUnload);

    if (this.$route.params.id) {
      await this.fetchDetail();
    }
    this.setOrganizationDataProtection();
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.isThereUnsavedChange) {
      next();
      return;
    }
    const answer = await this.confirm(this.reloadMessage);
    if (answer) {
      next();
    } else {
      next(false);
    }
  },

  methods: {
    goBack() {
      this.$router.push(`/${this.currentProduct.toLowerCase()}/data-breach`);
    },
    rightPanelClosed() {
      this.setOrganizationDataProtection();
      this.$v.form.$touch();
    },
    resetForm() {
      this.form = { name: undefined, sourceAndType: [], risks: [], dataCategories: [], dataSubjects: [] };
    },
    async fetchDetail() {
      const id = this.$route.params.id;
      try {
        await this.$store.dispatch("organizationDataBreach/fetchDetail", id);
        this.$store.commit("organizationDataBreach/editedEntity", this.dataBreach);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    beforeUnload(e) {
      if (!this.isThereUnsavedChange) {
        return undefined;
      }

      (e || window.event).returnValue = this.reloadMessage;
      return this.reloadMessage;
    },
    async setOrganizationDataProtection() {
      if (!this.form.dataProtectiontitleName) {
        this.form = {
          ...this.form,
          dataProtectionName: this.currentOrganization.title,
        };
      }
      if (!this.form.dataProtectionAddress) {
        this.form = {
          ...this.form,
          dataProtectionAddress: this.currentOrganization.address,
        };
      }

      this.roleWatcher();
    },
    roleWatcher() {
      const v = this.form.role;
      if (v === "ORGANIZATION_DATA_PROTECTION_PERSON") {
        this.form.detectedBy = this.currentOrganization.dataProtectionName ? this.currentOrganization.dataProtectionName : null;
        this.form.email = this.currentOrganization.dataProtectionAdress ? this.currentOrganization.dataProtectionAdress : null;
      } else if (v === "ORGANIZATION_CONTACT_PERSON") {
        this.form.detectedBy = this.currentOrganization.contactName ? this.currentOrganization.contactName : null;
        this.form.email = this.currentOrganization.contactAddress ? this.currentOrganization.contactAddress : null;
      } else if (v == "OTHER") {
        this.form.detectedBy = this.currentAccount.name;
        this.form.email = this.currentAccount.credential.email;
      }
    },
    async dataSubjectFilterSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDatasubject/paginate`, {
        options: { limit: 15 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    dataSubjectFilterChanged(result) {
      this.form = {
        ...this.form,
        dataSubjects: [...result],
      };
    },

    dataCategoryFilterChanged(result) {
      this.form = {
        ...this.form,
        dataCategories: [...result],
      };
    },

    async dataCategoryFilterSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationDataCategory/paginate`, {
        options: { limit: 15 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },

    notificationTypeFilterChanged(result) {
      this.form = {
        ...this.form,
        notificationType: result,
      };
    },

    async notificationTypeFilterSearch(value, callback) {
      const response = await this.$store.dispatch(`notificationType/paginate`, {
        options: { limit: 15 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },

    processingAdjectiveFilterChanged(result) {
      this.form = {
        ...this.form,
        processingAdjective: result,
      };
    },

    async processingAdjectiveFilterSearch(value, callback) {
      const response = await this.$store.dispatch(`processingAdjective/paginate`, {
        options: { limit: 15 },
        query: { name: { value } },
      });

      callback(response.data?.docs ?? []);
    },

    possibleEffectDefinitionSelected(selections) {
      this.form = {
        ...this.form,
        possibleEffect: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    effectedSystemsDefinitionSelected(selections) {
      const joined = selections.map((i) => i.name[this.currentLanguage]).join(",\r");
      this.form = {
        ...this.form,
        effectedSystems: this.form.effectedSystems ? this.form.effectedSystems + "\r" + joined : joined,
      };
    },
    beforePolicyDefinitionSelected(selections) {
      this.form = {
        ...this.form,
        beforePolicies: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    afterPolicyDefinitionSelected(selections) {
      this.form = {
        ...this.form,
        afterPolicies: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    sourceAndTypeDefinitionSelected(selections) {
      this.form = {
        ...this.form,
        sourceAndType: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    domesticInformationSelected(selections) {
      this.form = {
        ...this.form,
        domesticInformationSelected: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    abroadInformationSelected(selections) {
      this.form = {
        ...this.form,
        abroadInformationSelected: selections.map((i) => i.name[this.currentLanguage]).join(",\r"),
      };
    },
    riskSelected(selections) {
      this.form = {
        ...this.form,
        risks: [...selections],
      };
    },

    async onCountriesSearch(value, callback) {
      const lang = "tr";
      const response = Countries.filter((x) => x.name[lang].toLowerCase().includes(value.toLowerCase()));
      callback(response);
    },
    onCountriesSelect(item) {
      this.form = { ...this.form, breachLocation: item };
    },

    isDownloading(id) {
      return this.$store.getters[`organizationDataBreach/isDownloading`](id);
    },
    async download(item) {
      this.$store.commit("fileManager/addDownloadedFile", { file: item });
    },

    async exportAsWord() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        const { data: file } = await this.$store.dispatch(`organizationDataBreach/prepareWordExport`, this.$route.params.id);
        this.download(file);

        this.$toast.success(this.$t("common.success"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async save() {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      this.$v.$touch();
      try {
        if (this.$v.$error) {
          this.$handleHttpErrorByStatusCode(error, { customMessage: this.$t("common.validation") });
          return;
        }

        if (this.currentProduct) {
          this.form.products = [this.currentProduct];
        }

        await this.$store.dispatch("organizationDataBreach/save", this.form);
        this.isThereUnsavedChange = false;
        this.$toast.success(this.$t("common.success"));

        this.$router.push(`/${this.currentProduct.toLowerCase()}/data-breach`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
